import { useCallback } from "react";
import { useStore } from "react-redux";
import type { ReduxRootState } from "types/redux-store";

import { getUser } from "selectors/commonSelectors";
import {
  selectCurrentUser,
  type CurrentUserState,
} from "@/entities/current-user";
import { resolveReduxAppStore } from "@/entities/lib/redux-app-store";

import { User } from "features/user/types";

export type SupportedEventService = "AMP" | "HS";

type TrackEventProps = {
  eventName: string;
  metadata?: Record<string, any>;
  adminId?: string;
  userId?: string;
  useServices?: SupportedEventService[];
};

// Lazy-loaded references cached after first dynamic import
let _amplitudeTrack:
  typeof import("@amplitude/analytics-browser").track | null = null;
let _openobserveRum:
  typeof import("@openobserve/browser-rum").openobserveRum | null = null;

async function getAmplitudeTrack() {
  if (!_amplitudeTrack) {
    const mod = await import("@amplitude/analytics-browser");
    _amplitudeTrack = mod.track;
  }
  return _amplitudeTrack;
}

async function getOpenObserveRum() {
  if (!_openobserveRum) {
    const mod = await import("@openobserve/browser-rum");
    _openobserveRum = mod.openobserveRum;
  }
  return _openobserveRum;
}

// NOTE: this is port of public/components/app/services/event.service.js
function trackEvent({
  eventName,
  metadata = {},
  useServices = ["AMP"],
}: TrackEventProps) {
  console.info("[trackEvent]", eventName, metadata, useServices);

  if (process.env.NODE_ENV !== "production")
    return console.info("Not sent because not production env");

  if (useServices.includes("HS") && window._hsq) {
    try {
      // use hubspot "legacy events" to track events
      window._hsq.push([
        "trackEvent",
        {
          id: eventName,
        },
      ]);
    } catch (e) {
      console.error("Error trackEvent Hubspot", e);
    }
  }
  if (useServices.includes("AMP")) {
    getAmplitudeTrack()
      .then((track) => {
        track(eventName, metadata);
      })
      .catch((e) => {
        console.error("Error trackEvent Amplitude", e);
      });

    getOpenObserveRum()
      .then((rum) => {
        rum.addAction(eventName, metadata);
      })
      .catch((e) => {
        console.error("Error trackEvent OpenObserve RUM action", e);
      });
  }
}

function getCurrentUserFromRedux(): CurrentUserState {
  const appStore = resolveReduxAppStore();

  if (!appStore) {
    return {};
  }

  return selectCurrentUser(appStore.getState() as never);
}

export async function trackEventFx(
  params: TrackEventParams & Record<string, unknown>
) {
  const metadata = {
    ...(params.metadata ?? {}),
  };
  const user = getCurrentUserFromRedux();

  if (user) {
    metadata.plan = user.plan;
    metadata.role = user.role;
  }

  trackEvent({
    eventName: params.eventName,
    metadata,
    useServices: params.useServices,
  });
}

export const trackEventWithUser = (
  eventName: string,
  metadata?: Record<string, any>,
  user?: User,
  useServices?: SupportedEventService[]
): void => {
  if (!metadata) {
    metadata = {};
  }

  if (user) {
    metadata.plan = user.plan;
    metadata.role = user.role;
  }

  trackEvent({
    eventName,
    metadata,
    adminId: user && user.role === "admin" ? user.userId : user?.adminId,
    userId: user?.userId,
    useServices,
  });
};

export interface TrackEventParams {
  eventName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: Record<string, any>;
  useServices?: SupportedEventService[];
}

export function useTrackEvent() {
  const Store = useStore<ReduxRootState>();

  return useCallback(
    ({ eventName, metadata, useServices }: TrackEventParams) => {
      const user: User = getUser(Store.getState());

      trackEventWithUser(eventName, metadata, user, useServices);
    },
    [Store]
  );
}
